import { Component, DestroyRef, ElementRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { AuthService } from '@core/auth';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { fbString, showSpinner, TypedRoute } from '@shared/utils';
import { LoginService } from './login.service';
import { ToastService } from '@shared/services';
import { ProviderGeneralData } from '@shared/models';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AmplitudeEventService, FeatureFlagService, ProviderSettingsService } from '@core/services';
import { LoggedIn } from '@integrations/amplitude';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit{
  destroyRef = inject(DestroyRef);
  @ViewChild('emailInput') emailInput!: ElementRef;

  emailSent: boolean = false;
  email?: string;

  showDemoLogin: boolean = false; 
  showOnlyDemoLogin: boolean = false;
  signUpEnabled: boolean = false;
  providerData?: ProviderGeneralData;

  constructor(
    @Inject(ActivatedRoute) private route: TypedRoute<{ token: string }>,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private loginService: LoginService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
    private amplitudeEventService: AmplitudeEventService,
    private providerSettingsService: ProviderSettingsService,
  ) {
    this.route.data.subscribe((data) => {
      if (data.token) {
        this.authService
          .verifyLoginJWT(data.token)
          .pipe(showSpinner())
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (data) => {
              this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
                const redirectPath = params.get('redirect');
                this.amplitudeEventService.trackEvent(new LoggedIn());
                if (redirectPath) {
                  this.authService.logIn(data.key, redirectPath);
                } else {
                  this.authService.logIn(data.key);
                }
              });
            },
            error: (error: HttpErrorResponse) => {
              this.authService.showTokenVerifyErrorToast(error);
            },
          });
      }
    });
  }

  ngOnInit(): void {
    this.showDemoLogin = this.featureFlagService.getFeatureFlag('dev_enable_demo_login');
    this.showOnlyDemoLogin = this.featureFlagService.getFeatureFlag('dev_enable_demo_login_only');
    this.signUpEnabled = this.featureFlagService.getFeatureFlag('enable_signup');
    this.providerData = this.providerSettingsService.getProviderGeneralData();
  }

  logInForm = this.formBuilder.nonNullable.group({
    email: [fbString, [Validators.required, Validators.email]],
  });

  sendLogInEmail() {
    this.logInForm.markAllAsTouched();
    if (this.logInForm.valid) {
      this.email = this.logInForm.value.email!;
      this.loginService
        .sendLoginEmail(this.email)
        .pipe(showSpinner())
        .subscribe({
          next: () => {
            this.emailSent = true;
            this.toastService.add({
              message: this.translateService.instant('LogIn.WeHaveSentAnEmailToast', { email: this.email }),
              type: 'success',
            });
          },
          error: (error: HttpErrorResponse) => {
            if (error.status === 429) {
              this.toastService.add({
                message: error.error.message ?? this.translateService.instant('Common.Errors.UnknownError'),
                type: 'error',
              });
            } else {
              this.toastService.add({
                message: this.translateService.instant('LogIn.CouldNotSendEmail'),
                type: 'error',
              });
            }
          },
        });
    }
  }

  logIn(): void {
    this.authService.logIn('demotoken');
  }
}
